import React, { useEffect, useState } from 'react';
import Color from 'color';
import Icon from '../../Icon/Icon';
import LinkButton from '../../LinkButton/LinkButton';
import { createLink } from '../../../utils';
import { useGlobalValue } from '../../../state/GlobalProvider';
import { ExpandableWidget } from '../..';
import styles from './EntityHeader.module.sass';
import { Checkbox } from '../../inputs';

const renderEntry = (label, value, className, key, backgroundColor, color, icon, type = "") => {
  const LabelTag = typeof label === 'string' ? 'h5' : 'div';
  const bgColor = backgroundColor ? new Color(backgroundColor) : null;
  const input = color ? {
    color,
    opacity: 0.8,
    backgroundColor: bgColor ? bgColor.lighten(0.1).hex() : null,
    borderColor: 'transparent'
  } : null;
  const titleStyle = color ? {
    color: color
  } : null;
  const bgStyle = bgColor ? {
    backgroundImage: `linear-gradient(35deg, ${backgroundColor}, ${bgColor.rotate(-10).hex()})`
  } : null;
  return /*#__PURE__*/React.createElement("div", {
    key: key,
    className: styles['entry'],
    style: bgStyle
  }, icon && type !== "checkbox" && /*#__PURE__*/React.createElement("div", {
    className: styles['icon-container']
  }, /*#__PURE__*/React.createElement(Icon, {
    className: styles['icon'],
    name: icon
  })), /*#__PURE__*/React.createElement(LabelTag, {
    className: styles['label'],
    style: titleStyle
  }, label), /*#__PURE__*/React.createElement("div", {
    className: `${type !== "checkbox" && styles['input']} ${className || ''}`,
    style: input
  }, value || 'N/A'));
};

const renderCols = (row, className, key, params, proxy, i18n, modifiersClasses) => {
  const cols = (row || []).map((col, colIndex) => {
    if (!col) {
      return null;
    }

    const {
      label,
      value,
      type,
      backgroundColor,
      color,
      icon,
      checkboxLabel = ""
    } = col;
    let alignment = 'align-left';
    let labelElement = typeof label === 'string' ? i18n`${label || ''}` : label;
    let valueElement;

    switch (type) {
      case 'number':
        {
          alignment = alignment !== 'align-right' ? 'align-right' : alignment;
          valueElement = value && value != 'null' ? value : 'N/A';
          break;
        }

      case 'boolean':
        {
          valueElement = !!value === true ? i18n`Yes` : i18n`No`;
          break;
        }

      case 'date':
        {
          valueElement = value || "N/A";
          break;
        }

      case 'date_interval':
        {
          alignment = alignment !== 'align-center' ? 'align-center' : alignment;
          valueElement = value ? value.join(' - ') : "N/A";
          break;
        }

      case 'currency':
        {
          alignment = alignment !== 'currency align-right' ? 'currency align-right' : alignment;
          valueElement = value || 0;
          break;
        }

      case 'percentage':
        {
          alignment = alignment !== 'align-right' ? 'align-right' : alignment;
          valueElement = `${value || 0}%`;
          break;
        }

      case 'object':
        {
          if (value.id && value.name) {
            labelElement = /*#__PURE__*/React.createElement(LinkButton, {
              to: createLink(undefined, proxy, value, params),
              label: i18n`${label || ''}`
            });
            valueElement = value.name || '';
          } else {
            valueElement = null;
          }

          break;
        }

      case 'checkbox':
        {
          valueElement = /*#__PURE__*/React.createElement(Checkbox, {
            disabled: true,
            style: {
              marginTop: '10px'
            },
            value: typeof value === "string" ? parseInt(value, 10) : value,
            onChange: e => {
              setValue(event.target.checked);
            }
          }, i18n(checkboxLabel));
          labelElement = label && label.length !== 0 ? label : "‏‏‎ ‎";
          break;
        }

      default:
        {
          alignment = 'align-left';
          valueElement = `${value && value != 'null' ? value : 'N/A'}`;
          break;
        }
    }

    return renderEntry(labelElement, valueElement, alignment, colIndex, backgroundColor, color, icon, type);
  });
  return /*#__PURE__*/React.createElement("div", {
    key: key,
    className: `${className} ${modifiersClasses}`
  }, cols.length ? /*#__PURE__*/React.createElement("div", {
    className: `${styles['flex']} ${cols.length % 2 === 0 ? styles['even'] || '' : styles['odd'] || ''}`
  }, cols) : null);
};

const renderFieldSet = (fieldset, key, params, proxy, i18n, modifiersClasses) => {
  const groupItems = (fieldset.rows || []).map((row, index) => renderCols(row, styles['fieldset-group'], `fieldset_${index}`, params, proxy, i18n));
  return /*#__PURE__*/React.createElement(ExpandableWidget, {
    key: key,
    className: `${styles['fieldset']} ${modifiersClasses}`,
    title: i18n`${fieldset.title}`
  }, groupItems);
};

const EntityHeader = props => {
  const {
    data,
    modifiers,
    hasData
  } = props;
  const {
    proxy,
    i18n,
    params
  } = useGlobalValue();
  const modifiersClasses = (modifiers || []).map(m => styles[m]).filter(m => m).join(' ');
  const headerRows = data || [];
  const rows = headerRows.map((row, index) => {
    if (!row) {
      return null;
    }

    let className = index === 1 ? styles['grid'] : styles['group-content'];

    if (Array.isArray(row)) {
      return renderCols(row, className, index, params, proxy, i18n, modifiersClasses);
    } else if (typeof row === 'object') {
      return renderFieldSet(row, index, params, proxy, i18n, modifiersClasses);
    }

    return null;
  });
  return /*#__PURE__*/React.createElement("div", {
    className: `${styles['root']} ${modifiersClasses}`
  }, hasData === "no" ? /*#__PURE__*/React.createElement("div", {
    className: `placeholder ${styles['empty-content']}`
  }, /*#__PURE__*/React.createElement("small", null, i18n`There are no results for selected fiscal year!`)) : rows);
};

export default EntityHeader;